import React, { useEffect } from 'react';
import { Button, Space, message, Modal, Card, Row, Col } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { ArrowLeftOutlined, PlusOutlined } from '@ant-design/icons';
import moment from 'moment';

// Redux Actions
import {
  fetchOfflineBookings,
  deleteOfflineBooking,
  updateOfflineBooking,
  confirmOfflineBooking,
  cancelOfflineBooking
} from './OfflineBookingActions';

// Subcomponents
import BookingsList from './components/BookingsList';

// Styles
import '../../../assets/scss/components/OfflineBooking/OfflineBooking.scss';

const OfflineBookingListPage = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  // Redux State
  const {
    bookings = [],
    loading,
    error,
    success
  } = useSelector(state => state.offlineBooking || {});

  // Load bookings on mount
  useEffect(() => {
    dispatch(fetchOfflineBookings());
  }, [dispatch]);

  // Handle error messages
  useEffect(() => {
    if (error) {
      message.error(error);
    }
  }, [error]);

  // Note: Success messages are handled in individual actions to avoid duplicates

  // Refresh bookings list
  const handleRefresh = () => {
    dispatch(fetchOfflineBookings());
  };

  // Handle Edit - Navigate back to main form with record data
  const handleEdit = (record) => {
    // Store the record in navigation state to be picked up by OfflineBooking component
    history.push('/dashboard/booking/offline', {
      editRecord: record,
      editMode: true
    });
  };

  // Handle Delete
  const handleDelete = (id) => {
    Modal.confirm({
      title: 'Delete Offline Booking',
      content: 'Are you sure you want to delete this offline booking? This action cannot be undone.',
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await dispatch(deleteOfflineBooking(id));
          // Success message is shown in the action itself
          dispatch(fetchOfflineBookings());
        } catch (err) {
          message.error(err.message || 'Failed to delete offline booking');
        }
      }
    });
  };

  // Handle Confirm Booking
  const handleConfirmBooking = (record) => {
    Modal.confirm({
      title: 'Confirm Offline Booking',
      content: `Are you sure you want to confirm booking ${record.bookingRef || record.id}? This will mark the booking as CONFIRMED.`,
      okText: 'Yes, Confirm',
      okType: 'primary',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await dispatch(confirmOfflineBooking(record.id || record._id));
          message.success('Booking confirmed successfully');
          dispatch(fetchOfflineBookings());
        } catch (err) {
          message.error(err.message || 'Failed to confirm booking');
        }
      }
    });
  };

  // Handle Cancel Booking
  const handleCancelBooking = (record) => {
    // Check if booking has completed payment to show refund message
    const bookingId = record.id || record._id;
    const hasCompletedPayment = record.bookingStatus === 'CONFIRMED'; // CONFIRMED usually means payment is completed
    
    const modalContent = hasCompletedPayment 
      ? `Are you sure you want to cancel booking ${record.bookingRef || record.id}? 
         
This booking has a completed payment. A full refund will be automatically initiated when you cancel this booking.

Note: This action cannot be undone.`
      : `Are you sure you want to cancel booking ${record.bookingRef || record.id}? This action cannot be undone.`;
    
    Modal.confirm({
      title: 'Cancel Offline Booking',
      content: modalContent,
      okText: hasCompletedPayment ? 'Yes, Cancel & Refund' : 'Yes, Cancel Booking',
      okType: 'danger',
      cancelText: 'No, Keep Booking',
      width: hasCompletedPayment ? 520 : 416,
      onOk: async () => {
        try {
          await dispatch(cancelOfflineBooking(bookingId));
          
          const successMessage = hasCompletedPayment 
            ? 'Booking cancelled successfully. Refund has been initiated automatically.' 
            : 'Booking cancelled successfully';
            
          message.success(successMessage, 5);
          dispatch(fetchOfflineBookings());
        } catch (err) {
          message.error(err.message || 'Failed to cancel booking');
        }
      }
    });
  };

  // Navigate back to main form
  const handleBackToForm = () => {
    history.push('/dashboard/booking/offline');
  };

  return (
    <div className="offline-booking-container">
      {/* Page Header */}
      <div className="offline-booking-header">
        <h1>Offline Booking Management - List View</h1>
        <div className="header-actions">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleBackToForm}
          >
            Create New Booking
          </Button>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => history.goBack()}
          >
            Go Back
          </Button>
        </div>
      </div>

      {/* Bookings List */}
      <Row gutter={24}>
        <Col xs={24}>
          <Card
            className="offline-booking-form-card"
            loading={loading}
          >
            <BookingsList
              bookings={bookings}
              loading={loading}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onRefresh={handleRefresh}
              onConfirmBooking={handleConfirmBooking}
              onCancelBooking={handleCancelBooking}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default OfflineBookingListPage;
